import {
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tag,
  TagCloseButton,
  TagLabel,
  Textarea,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { ChangeEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaPaperclip, FaUpload } from "react-icons/fa";
import { ViewDocumentAsOwnerDto, bffApi, useCreateContractMessageMutation } from "../../../../autogen/bff-api";
import { pollForUploadedDocumentId } from "../../../../common/documents/pollForUploadedDocumentId";
import { useDocumentsUploader } from "../../../../common/documents/UppyUploader/useDocumentsUploader";
import { useApiError } from "../../../../common/errors/useApiError";
import { v4 as uuid } from "uuid";
import { useAppDispatch } from "../../../../common/redux/hooks";

interface Props {
  contractId: string;
  documents: ViewDocumentAsOwnerDto[];
  onSent: () => void;
}

interface UploadedAttachment {
  documentId: string;
  fileName: string;
}

export const MessageComposer = ({ contractId, documents, onSent }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const displayer = useApiError();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [text, setText] = useState("");
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);
  const [uploadedAttachments, setUploadedAttachments] = useState<UploadedAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const [createMessage, { isLoading: isSending }] = useCreateContractMessageMutation();
  const { upload } = useDocumentsUploader({
    entityType: "ContractMessageAttachment",
    entityId: contractId,
    refetch: () => undefined,
  });

  const canSend = text.trim().length > 0 || selectedDocumentIds.length > 0 || uploadedAttachments.length > 0;
  const availableDocuments = documents.filter((d) => !selectedDocumentIds.includes(d.id));

  const pollForDocumentId = (uploadId: string): Promise<string | null> =>
    pollForUploadedDocumentId(
      (uid) => {
        // Release the RTK Query subscription after each poll so the ~30 forceRefetch dispatches per upload
        // don't accumulate subscriptions for the whole poll window.
        const subscription = dispatch(
          bffApi.endpoints.getContractMessageAttachmentUpload.initiate({ contractId, uploadId: uid }, { forceRefetch: true })
        );
        return subscription
          .unwrap()
          .then((result) => result.documentId ?? null)
          .catch(() => null)
          .finally(() => subscription.unsubscribe());
      },
      uploadId,
    );

  const onFilePicked = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setIsUploading(true);
    try {
      const uploadId = uuid();
      const extension = file.name.includes(".") ? file.name.split(".").pop() ?? "" : "";
      const key = await upload({ id: uploadId, name: file.name, extension, file });
      if (!key) return;
      const documentId = await pollForDocumentId(uploadId);
      if (documentId) {
        setUploadedAttachments((current) => [...current, { documentId, fileName: file.name }]);
      } else {
        displayer.trigger({ message: t("Could not attach the uploaded file. Please try again.") });
      }
    } finally {
      setIsUploading(false);
    }
  };

  const send = async () => {
    if (!canSend) return;
    try {
      await createMessage({
        contractId,
        createContractMessageRequest: {
          text: text.trim(),
          attachmentDocumentIds: [...selectedDocumentIds, ...uploadedAttachments.map((a) => a.documentId)],
        },
      }).unwrap();
      setText("");
      setSelectedDocumentIds([]);
      setUploadedAttachments([]);
      onSent();
    } catch (e) {
      displayer.trigger(e);
    }
  };

  return (
    <Flex direction="column" gap="2" mt="2">
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t("Write a message to the counterparty") ?? ""}
        bg="smBackground"
        rows={3}
      />
      {(selectedDocumentIds.length > 0 || uploadedAttachments.length > 0) && (
        <Wrap>
          {selectedDocumentIds.map((id) => (
            <WrapItem key={id}>
              <Tag>
                <TagLabel>{documents.find((d) => d.id === id)?.name ?? id}</TagLabel>
                <TagCloseButton onClick={() => setSelectedDocumentIds((ids) => ids.filter((x) => x !== id))} />
              </Tag>
            </WrapItem>
          ))}
          {uploadedAttachments.map((a) => (
            <WrapItem key={a.documentId}>
              <Tag>
                <TagLabel>{a.fileName}</TagLabel>
                <TagCloseButton
                  onClick={() =>
                    setUploadedAttachments((current) => current.filter((x) => x.documentId !== a.documentId))
                  }
                />
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      )}
      <Flex justifyContent="space-between" alignItems="center" gap="2">
        <Flex gap="2">
          <Menu>
            <MenuButton
              as={Button}
              variant="outline"
              size="sm"
              leftIcon={<FaPaperclip />}
              isDisabled={availableDocuments.length === 0}
            >
              {t("Attach from contract documents")}
            </MenuButton>
            <MenuList maxH="300px" overflowY="auto">
              {availableDocuments.map((d) => (
                <MenuItem key={d.id} onClick={() => setSelectedDocumentIds((ids) => [...ids, d.id])}>
                  {d.name}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<FaUpload />}
            isLoading={isUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {t("Upload from disk")}
          </Button>
          <input ref={fileInputRef} type="file" hidden onChange={(e) => void onFilePicked(e)} />
        </Flex>
        <Button
          onClick={() => void send()}
          isLoading={isSending}
          isDisabled={!canSend || isUploading}
          bg="smPrimary"
          color="white"
          _hover={{ opacity: 0.9 }}
        >
          {t("Send")}
        </Button>
      </Flex>
    </Flex>
  );
};
